import { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
// @mui
import { styled } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
//
import Header from './header';
import Nav from './nav';
import { SimulationBanner } from '../../components/ViewAsRoleSelector';
import FirstTimePasswordModal from '../../components/FirstTimePasswordModal';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
  '@media print': {
    display: 'block !important',
    minHeight: 'auto !important',
    overflow: 'visible !important',
    width: '100% !important',
  },
});

const Main = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isNavCollapsed',
})(({ theme, isNavCollapsed }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  transition: theme.transitions.create(['margin', 'padding'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  '@media print': {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    margin: '0 !important',
    overflow: 'visible !important',
    minHeight: 'auto !important',
    height: 'auto !important',
    width: '100% !important',
    maxWidth: '100% !important',
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { user, isLoggedIn } = useSelector((state) => state.auth || {});

  const isDesktop = useResponsive('up', 'lg');

  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    return localStorage.getItem('mtc_sidebar_collapsed') === 'true';
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleToggleNav = () => {
    if (isDesktop) {
      setDesktopCollapsed((prev) => {
        const next = !prev;
        localStorage.setItem('mtc_sidebar_collapsed', String(next));
        return next;
      });
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  // Keyboard shortcut Ctrl+B or Cmd+B to toggle sidebar collapse
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        handleToggleNav();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const isNavCollapsed = isDesktop && desktopCollapsed;

  // Enforce session authentication across all dashboard routes
  if (!user && !isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return (
    <StyledRoot>
      <div className="no-print">
        <Header
          isNavCollapsed={isNavCollapsed}
          onToggleNav={handleToggleNav}
        />

        <Nav
          openNav={mobileOpen}
          onCloseNav={() => setMobileOpen(false)}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={handleToggleNav}
        />
      </div>

      <Main isNavCollapsed={isNavCollapsed}>
        <div className="no-print">
          <SimulationBanner />
          <FirstTimePasswordModal />
        </div>
        <Outlet />
      </Main>
    </StyledRoot>
  );
}
